import React, { useContext, useState } from "react";
import { useLocation } from "react-router-dom";
import { noop } from "lodash";
import { observer } from "mobx-react-lite";
import { useEffectOnce } from "@lib-shared/hooks";
import { AppContext } from "core/context";
import { logApiError } from "core/lib";
import { FreshDeskService } from "core/services/fresh-desk.service";
import ModalStore from "core/stores/modal.store";
import TicketStoreManager from "core/stores/ticket.store.manager";
import { ShowModal, ModalAlert, ModalIcon, PleaseWait } from "components/common-ui";
import NewQueryForm from "./components/new-query-form";
import QueriesList from "./components/queries-list";
import QueryDetails from "./components/query-details";
import ModalDialog from "components/ui/modal-dialog";

type ListQueriesProps = {
  auth: any,
  dataManager: TicketStoreManager
}

const newQueryModalStore = new ModalStore({
  cssClassName: "new-query",
  showClose: false
});

const queryDetailsModalStore = new ModalStore({
  cssClassName: "custom-modal query-details-modal"
});

const ListQueries = observer((props: ListQueriesProps) => {
  const { auth, dataManager } = props;
  const appContext = useContext(AppContext);
  const newQueryFormId = "new-query-form";
  const [isLoggingQuery, setIsLoggingQuery] = useState(false);
  const [showQueryDetails, setShowQueryDetails] = useState(false);
  const [ticketId, setTicketId] = useState("");

  const pageTitle = "Queries";

  const store = dataManager.store;

  //routing
  const location = useLocation();
  const query = new URLSearchParams(location.search);

  const filterBy = query.get(store.filter);

  const api = new FreshDeskService();

  let existingDialog: any = null;

  const modalStore = newQueryModalStore;

  const ticketStore = queryDetailsModalStore;

  const updateFilter = (filter: string) => {
    store.updateFilter(filter);
  }

  const closeModal = () => {
    if (existingDialog != null) {
      ModalAlert.close(existingDialog);
    }
  }

  const showModal = (title: string, text: string, isError = false, showButtons = false, useDefaultCloseAction = true, appendSpinner = false) => {
    closeModal();

    const options = {
      title: title,
      body: text,
      showButton: showButtons,
      showClose: false,
      icon: isError ? ModalIcon.error : ModalIcon.tick,
      onClose: noop,
      appendSpinner: appendSpinner
    };

    if (useDefaultCloseAction) {
      options.onClose = async () => await handleModalCloseAsync()
    }

    existingDialog = ShowModal(options);
  }

  const handleFilterChange = (e: any, status: string) => {
    e?.preventDefault();
    updateFilter(status);
  }

  const loadQueriesAsync = async (clearCache = false) => {
    await dataManager.loadTicketsAsync(clearCache);

    if (dataManager.store.hasError) {
      ModalAlert.error("Failed to fetch logged query cases");
    }
  }

  useEffectOnce(() => {
    const onLoad = async () => {
      appContext.updateTitle(pageTitle);
      appContext.updateHeader(pageTitle);

      const param = new URLSearchParams(window.location.search).get("id");

      await loadQueriesAsync();

      updateFilter(filterBy ? filterBy : "");

      if (param) {
        onSelectTicket(null, param);
      }
    }

    onLoad();
  });

  const handleNewQuery = () => setIsLoggingQuery(true);

  const handleModalCloseAsync = async () => {
    setIsLoggingQuery(false);

    closeModal();
  }

  const handleLogQueryFormClose = () => setIsLoggingQuery(false);

  const submitQueryAsync = async (caseData: any) => {
    showModal("Submitting Query", "", false, false, true, true);

    try {
      const response = await api.createTicketAsync(caseData);

      if (response?.data) {
        showModal("That was easy!", "Your query has successfully been logged.", false, true);

        await loadQueriesAsync(true);
      }
    } catch (error) {
      logApiError(error);

      showModal("Error", "Sorry something went wrong while submitting your query.", true, true, false);
      return false;
    }

    setIsLoggingQuery(false);

    return true;
  }

  const Filter = () => {
    if (!store.hasTickets) {
      return null;
    }

    const filterLink = (value: string, name: string) => {
      const extraCss = store.filter === value ? " active" : "";

      return (
        <li className={extraCss} onClick={(e) => {
          handleFilterChange(e, value);
        }}>
          {name}
        </li>
      );
    }

    return (
      <section className="header">
        <ul className="support-filters">
          {filterLink("", "All")}
          {filterLink("open", "Open")}
          {filterLink("closed", "Closed")}
        </ul>

        <button className="btn btn-primary" onClick={handleNewQuery}>
          New Query
        </button>
      </section>
    );
  }

  const NoQueriesMessage = () => {
    if (!store.hasLoaded || store.hasTickets || store.isLoading || isLoggingQuery) {
      return null;
    }

    return (
      <ul className="info-rows no-queries">
        <li>
          <span>
            <h5>
              <i className="icon-fix-errors" />
              You haven't logged any queries
            </h5>

            Submit a new query so we can help you.
          </span>

          <button
            className="btn btn-primary"
            onClick={handleNewQuery}
          >
            New Query</button>
        </li>
      </ul>
    );
  }

  const onSelectTicket = (e: any, id: string) => {
    setTicketId(id);
    setShowQueryDetails(true);
  }

  const closeTicket = () => {
    setTicketId("");
    setShowQueryDetails(false);
  }

  modalStore.updateOnClose(handleLogQueryFormClose);
  modalStore.updateDisplay(isLoggingQuery);

  ticketStore.updateDisplay(showQueryDetails);

  if (store.isLoading) {
    return <PleaseWait message="Getting queries" />
  }

  return (
    <div className="case-management">
      <Filter />

      <QueriesList onSelectTicket={onSelectTicket} store={store} />

      <NoQueriesMessage />

      <ModalDialog store={modalStore}>
        <NewQueryForm
          id={newQueryFormId}
          isLoading={store.isLoading}
          display={true}
          onSubmit={async (data: any) => await submitQueryAsync(data)}
          onClose={handleLogQueryFormClose} />
      </ModalDialog>

      <ModalDialog store={ticketStore}>
        <QueryDetails auth={auth} id={ticketId} onClose={closeTicket} />
      </ModalDialog>
    </div>
  );
});

export default ListQueries;
